import { Injectable, signal, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

/**
 * Service for managing user's preferred locale with reactive updates
 * Uses Angular Signals for efficient reactivity
 */
@Injectable({
  providedIn: 'root',
})
export class UserLocaleService implements OnDestroy {
  private readonly storageKey = 'language';
  private readonly supportedLocales = new Set(['es', 'es-cl', 'en', 'fr', 'pt', 'cht']);
  private readonly fallbackLocale = 'es';
  private readonly _currentLocale = signal<string>('es');
  readonly currentLocale = this._currentLocale.asReadonly();
  private readonly destroy$ = new Subject<void>();

  constructor(private translateService: TranslateService) {
    this.initializeLocale();
  }

  private initializeLocale(): void {
    const storedLocale = this.getStoredLocale();
    const currentLang =
      storedLocale ||
      this.normalizeLocale(this.translateService.currentLang) ||
      this.normalizeLocale(this.translateService.defaultLang) ||
      this.normalizeLocale(this.getBrowserLocale()) ||
      this.fallbackLocale;

    this._currentLocale.set(currentLang);
    this.translateService.setDefaultLang(this.fallbackLocale);

    // Preload fallback translations ONE TIME at initialization
    // This ensures missing keys fallback to ES without repeated HTTP requests
    this.translateService.getTranslation(this.fallbackLocale)
      .pipe(take(1))
      .subscribe({
        error: () => {
          console.warn(`Failed to preload fallback language: ${this.fallbackLocale}`);
        }
      });

    if (this.translateService.currentLang !== currentLang) {
      this.translateService.use(currentLang);
    }

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        const normalized = this.normalizeLocale(event.lang) || 'es';
        this._currentLocale.set(normalized);
        this.persistLocale(normalized);
      });
  }

  /**
   * Updates the locale of the user
   * Called when user changes language in settings
   * @param locale New locale code (e.g., 'es', 'en', 'fr', 'pt', 'cht')
   */
  setLocale(locale: string): void {
    const normalized = this.normalizeLocale(locale) || this.fallbackLocale;

    this.translateService.setDefaultLang(this.fallbackLocale);

    this.translateService.use(normalized)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this._currentLocale.set(normalized);
          this.persistLocale(normalized);
        },
        error: (err) => {
          this._currentLocale.set(normalized);
          this.persistLocale(normalized);
          console.warn(`Error loading language '${normalized}':`, err);
        }
      });
  }

  /**
   * Gets current locale value (non-reactive)
   * @returns Current locale string
   */
  getCurrentLocale(): string {
    return this._currentLocale();
  }

  private getStoredLocale(): string | null {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return this.normalizeLocale(stored) || null;
    } catch {
      return null;
    }
  }

  private persistLocale(locale: string): void {
    try {
      localStorage.setItem(this.storageKey, locale);
    } catch {
    }
  }

  private getBrowserLocale(): string | null {
    if (typeof navigator === 'undefined') return null;
    return navigator.language || null;
  }

  private normalizeLocale(locale: string | null | undefined): string | null {
    if (!locale) return null;
    const lower = locale.toLowerCase();
    if (lower.startsWith('zh') || lower.includes('hant')) return 'cht';
    if (this.supportedLocales.has(lower)) return lower;
    const base = lower.split('-')[0];
    if (this.supportedLocales.has(base)) return base;
    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
